import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  MenuItem,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
  makeStyles
} from '@material-ui/core'
import DeleteOutlineIcon from '@material-ui/icons/DeleteOutline'
import Dayjs from 'dayjs'
import React from 'react'

import {
  createRmRegisterLine,
  deleteRmRegisterLine,
  fetchMyCentres,
  fetchRmRegister,
  updateRmRegisterLine
} from 'api/procurement'
import constants from 'app/constants'
import { confirm, eventAlert, notify } from 'app/helpers'

import {
  SearchHeadCell,
  useColumnSearch
} from 'app/components/generic/SearchHeadCell'
import AttachmentUploader from '../components/AttachmentUploader'
import { REPAIR_MAINTENANCE_CATEGORIES, orderCentres } from '../constants'

// Azam's R&M register (Satya, 4 Nov 2026). Azam pays for some centre repairs
// himself; this is his own ledger across all centres. Every line belongs to a
// centre and counts against that centre's R&M budget. Lines a centre files as "Paid by: Azam" show here too,
// so the same spend is never entered twice.

const useStyles = makeStyles((theme) => ({
  root: { padding: theme.spacing(2), width: '100%', boxSizing: 'border-box' },
  toolbar: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(2),
    marginBottom: theme.spacing(2),
    flexWrap: 'wrap'
  },
  spacer: { flexGrow: 1 },
  stats: {
    display: 'flex',
    gap: theme.spacing(2),
    flexWrap: 'wrap',
    marginBottom: theme.spacing(2)
  },
  stat: {
    padding: theme.spacing(1, 2),
    borderRadius: 8,
    background: theme.palette.grey[100],
    minWidth: 160
  },
  statValue: { fontWeight: 600, fontSize: 18 },
  wrapCells: { '& td': { whiteSpace: 'normal', wordBreak: 'break-word' } },
  noSpin: {
    '& input[type=number]': { MozAppearance: 'textfield' },
    '& input[type=number]::-webkit-outer-spin-button': {
      WebkitAppearance: 'none',
      margin: 0
    },
    '& input[type=number]::-webkit-inner-spin-button': {
      WebkitAppearance: 'none',
      margin: 0
    }
  }
}))

// What each searchable column shows, for the one-row search header.
const SEARCH_COLS = {
  date: (r) => Dayjs(r.date).format('D MMM YYYY'),
  centre: (r) => r.centre_name,
  category: (r) => r.category,
  amount: (r) => r.amount,
  description: (r) => r.description,
  entered_by: (r) => r.created_by_name
}

const rupees = (v) => `₹${Number(v || 0).toLocaleString('en-IN')}`

const blankLine = (month) => {
  const today = Dayjs()
  return {
    centre: '',
    date:
      month && month !== today.format('YYYY-MM')
        ? `${month}-01`
        : today.format('YYYY-MM-DD'),
    category: '',
    amount: '',
    description: ''
  }
}

const payload = (l) => ({
  centre: l.centre,
  date: l.date,
  category: l.category,
  amount: Number(l.amount),
  description: l.description || ''
})

const problem = (l, month) => {
  if (!l.centre) return 'Pick the centre'
  if (!l.category) return 'Pick a category'
  if (!(Number(l.amount) > 0)) return 'Enter the amount'
  if (Dayjs(l.date).format('YYYY-MM') !== month)
    return `The date must be in ${Dayjs(`${month}-01`).format('MMMM YYYY')}`
  return ''
}

// The editable fields for one line, used by the add row and the edit dialog.
const LineFields = ({ value, onChange, centres, column }) => (
  <Box
    display="flex"
    flexDirection={column ? 'column' : 'row'}
    flexWrap="wrap"
    alignItems={column ? 'stretch' : 'flex-start'}
    style={{ gap: 12 }}>
    <TextField
      select
      size="small"
      variant="outlined"
      label="Centre"
      style={{ minWidth: 170 }}
      value={value.centre || ''}
      onChange={(e) => onChange({ centre: e.target.value })}>
      {centres.map((c) => (
        <MenuItem key={c.uuid} value={c.uuid}>
          {c.name}
        </MenuItem>
      ))}
    </TextField>
    <TextField
      type="date"
      size="small"
      variant="outlined"
      label="Date"
      style={{ width: 160 }}
      value={value.date || ''}
      onChange={(e) => onChange({ date: e.target.value })}
      InputLabelProps={{ shrink: true }}
    />
    <TextField
      select
      size="small"
      variant="outlined"
      label="Category"
      style={{ minWidth: 220 }}
      value={value.category || ''}
      onChange={(e) => onChange({ category: e.target.value })}>
      {REPAIR_MAINTENANCE_CATEGORIES.map((c) => (
        <MenuItem key={c} value={c}>
          {c}
        </MenuItem>
      ))}
    </TextField>
    <TextField
      type="number"
      size="small"
      variant="outlined"
      label="Amount (₹)"
      style={{ width: 120 }}
      value={value.amount}
      onChange={(e) => onChange({ amount: e.target.value })}
      inputProps={{ min: 0, style: { textAlign: 'right' } }}
    />
    <TextField
      size="small"
      variant="outlined"
      label="Description"
      style={{ flex: column ? undefined : '1 1 220px' }}
      value={value.description || ''}
      onChange={(e) => onChange({ description: e.target.value })}
    />
  </Box>
)

const HqRmRegister = ({ initialMonth }) => {
  const classes = useStyles()
  const [month, setMonth] = React.useState(
    initialMonth || Dayjs().format('YYYY-MM')
  )
  const [centres, setCentres] = React.useState([])
  const [data, setData] = React.useState(null)
  const [loading, setLoading] = React.useState(false)
  const [line, setLine] = React.useState(blankLine(month))
  const [saving, setSaving] = React.useState(false)
  const [editLine, setEditLine] = React.useState(null)
  const [billLine, setBillLine] = React.useState(null)

  React.useEffect(() => {
    fetchMyCentres()
      .then((res) => setCentres(orderCentres(res?.results || [])))
      .catch(() => setCentres([]))
  }, [])

  const load = React.useCallback(async () => {
    setLoading(true)
    try {
      setData(await fetchRmRegister(month))
    } catch (error) {
      eventAlert(
        error?.message || 'Failed to load the register',
        constants.ERROR,
        'R&M register'
      )
      setData({ results: [], total: 0 })
    } finally {
      setLoading(false)
    }
  }, [month])
  React.useEffect(() => {
    load()
  }, [load])
  React.useEffect(() => {
    setLine((prev) => ({ ...prev, date: blankLine(month).date }))
  }, [month])

  const onAdd = async () => {
    const why = problem(line, month)
    if (why) {
      notify(why, '', 'warning')
      return
    }
    setSaving(true)
    try {
      const created = await createRmRegisterLine(payload(line))
      notify('Line added — attach the bill with 📎 Bill', '', 'success')
      // Keep centre + date for the next line; most entries come in batches.
      setLine({ ...blankLine(month), centre: line.centre, date: line.date })
      await load()
      if (created?.uuid) setBillLine(created)
    } catch (error) {
      eventAlert(error?.message || 'Could not add', constants.ERROR, 'R&M register')
    } finally {
      setSaving(false)
    }
  }

  const onSaveEdit = async () => {
    const why = problem(editLine, month)
    if (why) {
      notify(why, '', 'warning')
      return
    }
    try {
      await updateRmRegisterLine(editLine.uuid, payload(editLine))
      notify('Line updated', '', 'success')
      setEditLine(null)
      await load()
    } catch (error) {
      eventAlert(error?.message || 'Update failed', constants.ERROR, 'R&M register')
    }
  }

  const onDelete = (uuid) =>
    confirm(
      async () => {
        try {
          await deleteRmRegisterLine(uuid)
          notify('Line deleted', '', 'success')
          await load()
        } catch (error) {
          eventAlert(error?.message || 'Delete failed', constants.ERROR, 'R&M register')
        }
      },
      'Delete this line from the register?',
      () => {}
    )

  const allRows = data?.results || []
  const { rows, box, searching } = useColumnSearch(allRows, SEARCH_COLS)
  const byCentre = allRows.reduce((acc, r) => {
    acc[r.centre_name] = (acc[r.centre_name] || 0) + Number(r.amount)
    return acc
  }, {})

  return (
    <Paper className={`${classes.root} ${classes.noSpin}`} elevation={0}>
      <div className={classes.toolbar}>
        <Typography variant="h6">Azam's R&M register</Typography>
        <TextField
          type="month"
          label="Month"
          size="small"
          variant="outlined"
          value={month}
          onChange={(e) => setMonth(e.target.value)}
          InputLabelProps={{ shrink: true }}
        />
        <div className={classes.spacer} />
      </div>

      <div className={classes.stats}>
        <div className={classes.stat}>
          <Typography variant="caption" color="textSecondary">
            Paid by Azam — {Dayjs(`${month}-01`).format('MMMM YYYY')}
          </Typography>
          <div className={classes.statValue}>{rupees(data?.total)}</div>
        </div>
        {Object.keys(byCentre).length > 0 && (
          <div className={classes.stat}>
            <Typography variant="caption" color="textSecondary">
              By centre
            </Typography>
            {Object.entries(byCentre).map(([name, amt]) => (
              <Typography key={name} variant="body2">
                {name}: {rupees(amt)}
              </Typography>
            ))}
          </div>
        )}
      </div>

      {loading ? (
        <Box display="flex" justifyContent="center" p={4}>
          <CircularProgress />
        </Box>
      ) : (
        <Box width="100%">
          <Table
            size="small"
            className={classes.wrapCells}
            style={{ width: '100%', margin: 0, tableLayout: 'fixed' }}>
            <TableHead>
              <TableRow>
                <SearchHeadCell label="sl no." style={{ width: 56 }} />
                <SearchHeadCell
                  label="date"
                  search={box('date')}
                  style={{ width: 120 }}
                />
                <SearchHeadCell
                  label="centre"
                  search={box('centre')}
                  style={{ width: 140 }}
                />
                <SearchHeadCell
                  label="category"
                  search={box('category')}
                  style={{ width: 180 }}
                />
                <SearchHeadCell
                  label="amount (₹)"
                  align="right"
                  search={box('amount')}
                  style={{ width: 120 }}
                />
                <SearchHeadCell
                  label="description"
                  search={box('description')}
                />
                <SearchHeadCell
                  label="entered by"
                  search={box('entered_by')}
                  style={{ width: 140 }}
                />
                <SearchHeadCell
                  label="actions"
                  align="center"
                  style={{ width: 190 }}
                />
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((r, i) => (
                <TableRow key={r.uuid} hover>
                  <TableCell>{i + 1}</TableCell>
                  <TableCell>{Dayjs(r.date).format('D MMM YYYY')}</TableCell>
                  <TableCell>{r.centre_name}</TableCell>
                  <TableCell>{r.category || '—'}</TableCell>
                  <TableCell align="right">
                    {Number(r.amount).toLocaleString('en-IN')}
                  </TableCell>
                  <TableCell>{r.description || '—'}</TableCell>
                  <TableCell>{r.created_by_name || '—'}</TableCell>
                  <TableCell align="center">
                    <Button
                      size="small"
                      onClick={() =>
                        setEditLine({ ...r, amount: String(r.amount) })
                      }>
                      Edit
                    </Button>
                    <IconButton size="small" onClick={() => onDelete(r.uuid)}>
                      <DeleteOutlineIcon fontSize="small" />
                    </IconButton>
                    <Button size="small" onClick={() => setBillLine(r)}>
                      {r.invoice_count ? `📎 ${r.invoice_count}` : '📎 Bill'}
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
              {!rows.length && (
                <TableRow>
                  <TableCell colSpan={8}>
                    <Typography variant="body2" color="textSecondary">
                      {searching
                        ? 'No lines match the search.'
                        : 'Nothing paid by Azam this month yet.'}
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </Box>
      )}

      <Box mt={3} p={2} style={{ background: '#fafafa', borderRadius: 8 }}>
        <Typography style={{ fontWeight: 600, marginBottom: 12 }}>
          Add a line
        </Typography>
        <LineFields
          value={line}
          centres={centres}
          onChange={(patch) => setLine((p) => ({ ...p, ...patch }))}
        />
        <Box mt={2}>
          <Button
            variant="contained"
            color="primary"
            size="small"
            disabled={saving}
            onClick={onAdd}>
            {saving ? 'Saving…' : 'Add line'}
          </Button>
        </Box>
      </Box>

      <Dialog open={!!editLine} onClose={() => setEditLine(null)}>
        <DialogTitle>Edit line</DialogTitle>
        <DialogContent>
          {editLine && (
            <Box style={{ minWidth: 340 }}>
              <LineFields
                column
                value={editLine}
                centres={centres}
                onChange={(patch) => setEditLine((p) => ({ ...p, ...patch }))}
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditLine(null)}>Cancel</Button>
          <Button color="primary" variant="contained" onClick={onSaveEdit}>
            Save
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={!!billLine}
        onClose={() => {
          setBillLine(null)
          load()
        }}
        maxWidth="xs"
        fullWidth>
        <DialogTitle>
          Bill
          {billLine
            ? ` — ${billLine.centre_name}, ${Dayjs(billLine.date).format(
                'D MMM YYYY'
              )}, ${rupees(billLine.amount)}`
            : ''}
        </DialogTitle>
        <DialogContent>
          {billLine && (
            <AttachmentUploader
              entityType="petty_cash_entry"
              entry={billLine.uuid}
              label="Bill / receipt for this spend"
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setBillLine(null)
              load()
            }}>
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  )
}

export default HqRmRegister
